"use client"

import React from "react"
import { motion } from "framer-motion"
import Image from "next/image"
import { Plus, Trash2 } from "lucide-react"

function DocumentTemplateCard({ 
  doc, 
  index, 
  onUseTemplate, 
  onPreview,
  onDelete
}: { 
  doc: any; 
  index: number; 
  onUseTemplate?: (doc: any) => void; 
  onPreview?: (doc: any) => void;
  onDelete?: (id: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:shadow-lg hover:border-slate-300 duration-200"
    >
      {/* Thumbnail */}
      <div className="relative aspect-[1/1.414] w-full p-2 overflow-hidden cursor-pointer">
        <div className="relative w-full h-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <Image
            src={doc.thumbnailUrl || doc.documentTemplate?.thumbnailUrl || "/placeholder-template.png"}
            alt={doc.name}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-110"
          />
        </div>
        
        {/* Delete button for user templates */}
        {doc.source === "user" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Are you sure you want to delete this template?")) {
                onDelete?.(doc.templateId || doc._id);
              }
            }}
            className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-red-500 shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-50 hover:text-red-600 z-10 cursor-pointer"
            title="Delete template"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-2 bg-black/0 rounded-xl group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onPreview?.({
                    ...doc,
                    // Map document structure if needed by modal
                    pages: doc.pages || (doc.documentTemplate?.thumbnailUrl ? [{ id: "p1", thumbnailUrl: doc.documentTemplate.thumbnailUrl, name: "Page 1" }] : [])
                });
              }}
              className="px-4 py-2 w-full bg-white/90 backdrop-blur-sm rounded-full text-[11px] font-bold text-slate-800 shadow-lg hover:bg-white transition-all cursor-pointer"
            >
              Preview
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onUseTemplate?.(doc);
              }}
              className="px-4 py-2 w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[11px] font-bold transition-all hover:scale-105 active:scale-95 shadow-lg shadow-indigo-500/20 cursor-pointer"
            >
              Use Template
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col pb-3 pt-1 px-3">
        <h3 className="font-bold text-sm text-slate-900 line-clamp-1 cursor-pointer hover:text-blue-600 transition-colors">
          {doc.name}
        </h3>
        <p className="text-[11px] font-medium text-slate-500 capitalize">
          {doc.category || "Document"}
        </p>
      </div>
    </motion.div>
  )
}

function UploadCard({ onClick }: { onClick?: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={onClick}
      className="group relative flex flex-col items-center justify-center aspect-[1/1.414] rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/30 transition-all hover:bg-slate-50 hover:border-indigo-300 hover:shadow-lg cursor-pointer"
    >
      <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 group-hover:bg-indigo-50 transition-all duration-300">
        <Plus className="w-7 h-7 text-slate-400 group-hover:text-indigo-600" strokeWidth={2.5} />
      </div>
      <div className="mt-4 text-center px-4">
        <p className="text-[13px] font-bold text-slate-900">Upload New</p>
        <p className="text-[11px] text-slate-500 font-medium mt-1">Custom Template</p>
      </div>
    </motion.div>
  )
}

export function DocumentTemplates({ 
  templates = [], 
  onUseTemplate, 
  onPreview,
  showUpload = false,
  onUploadClick,
  onDelete
}: { 
  templates?: any[]; 
  onUseTemplate?: (doc: any) => void; 
  onPreview?: (doc: any) => void;
  showUpload?: boolean;
  onUploadClick?: () => void;
  onDelete?: (id: string) => void;
}) {
  if (templates.length === 0 && !showUpload) {
    return (
        <div className="col-span-full py-12 text-center">
            <p className="text-sm text-slate-400">No document templates found.</p>
        </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-x-3.5 gap-y-3.5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {showUpload && <UploadCard onClick={onUploadClick} />}
      {templates.map((doc, i) => (
        <DocumentTemplateCard key={`${doc.templateId || doc._id}-${i}`} doc={doc} index={i} onUseTemplate={onUseTemplate} onPreview={onPreview} onDelete={onDelete} />
      ))}
    </div>
  )
}
